import { Check, Inbox, Bot, CalendarDays, Smartphone, TrendingUp } from "lucide-react";
import { SectionHeader, Reveal } from "@/components/landing/shared";
import { SiteCTA } from "./shared";

const BENEFITS = [
  {
    icon: Inbox,
    title: "One inbox for every lead",
    desc: "Calls, texts, forms, Google messages — every lead lands in one place. No more digging through apps.",
  },
  {
    icon: Bot,
    title: "AI that answers and books",
    desc: "Replies in seconds, answers questions, and books the customer straight onto your calendar — even at 2am.",
  },
  {
    icon: CalendarDays,
    title: "Your calendar fills itself",
    desc: "Watch customers land in real time. You get a ping, they get a confirmation. Done.",
  },
  {
    icon: Smartphone,
    title: "Your own branded app",
    desc: "Every client gets their own app under their business name — leads, messages and results in one place. Log in from your phone or laptop.",
  },
];

// Site variant of the Dashboard section — "your own branded app" story with a
// book-a-call CTA instead of the old checkout modal.
export default function SiteApp() {
  return (
    <section data-testid="dashboard-section" className="py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHeader
          align="center"
          eyebrow="Your own app"
          title={<>Your own branded app — <span className="text-gradient">every lead in one place.</span></>}
          subtitle="You're not just getting a website. Every client gets their own branded app: one inbox for every lead, AI that texts back and books the customer, and a calendar that fills itself."
        />

        <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-navy/[0.06] bg-navy p-6 shadow-[0_24px_70px_-20px_rgba(14,18,48,0.35)] sm:p-8">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Your app · today</p>
                <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-[10.5px] font-bold text-emerald-300">3 new leads</span>
              </div>
              <div className="mt-5 space-y-3">
                {[
                  ["Maria G.", "Do you have openings this week?", "AI replied · booked Thu 10:00", true],
                  ["Dan R.", "How much for a water heater swap?", "AI replied · sent booking link", true],
                  ["Tasha W.", "Missed call", "AI texted back in 40 sec", true],
                ].map(([who, msg, status]) => (
                  <div key={who} className="rounded-2xl bg-white/[0.06] p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[13.5px] font-bold text-white">{who}</p>
                      <span className="text-[10.5px] font-semibold text-emerald-300">{status}</span>
                    </div>
                    <p className="mt-1 text-[12.5px] text-white/50">"{msg}"</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 rounded-2xl bg-brand-indigo/20 px-4 py-3">
                <TrendingUp className="h-4 w-4 text-brand-indigo" />
                <p className="text-[12.5px] font-semibold text-white/80">Booked customers this week: <span className="text-white">12</span> — up 3 from last week</p>
              </div>
            </div>
          </Reveal>

          <div className="space-y-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex gap-4 rounded-2xl border border-navy/8 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-14px_rgba(14,18,48,0.15)]">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-indigo/10">
                  <b.icon className="h-5 w-5 text-brand-indigo" />
                </span>
                <div>
                  <h3 className="font-display text-[15px] font-extrabold text-navy">{b.title}</h3>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-navy/55">{b.desc}</p>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1 text-[13px] font-semibold text-navy/50">
              {["Your logo, your name", "Phone + computer", "Your data stays yours"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" />{t}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <SiteCTA href="/contact" testId="dashboard-cta">See it on a free call</SiteCTA>
        </div>
      </div>
    </section>
  );
}
